/** eslint-env browser */
/** @jsxImportSource @emotion/react */
import * as React from 'react';

// eslint-disable-next-line no-unused-vars
import { jsx } from '@emotion/react';
import { components as defaultComponents } from 'react-select';
import tokens from '@gandi/ds-primitives';
import { useTranslator } from '@gandi/translate';
import { AddonButton, Spinner, Svg, Tag, TagList } from '@gandi/ui';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const theme = provided => ({
  ...provided,
  borderRadius: tokens.border.radius.small,
  spacing: {
    ...provided.spacing,
    menuGutter: 0
  },
  colors: {
    ...provided.colors,
    primary: tokens.color.neutral.strong,
    primary25: tokens.color.neutral['50'],
    danger: tokens.color.negative.plain
  }
});
const widgetHeight = variant => ({
  minHeight: variant === 'small' ? tokens.form.widget.height.small : tokens.form.widget.height.medium
});
const showSearchIcon = props => props.selectProps.isSearchable && props.selectProps.menuIsOpen && !(props.selectProps.isMulti && props.selectProps.value);
const showCursor = props => props.selectProps.isSearchable && props.selectProps.menuIsOpen;
const styles = {
  container: (provided, state) => ({
    ...provided,
    ...state.selectProps.style
  }),
  // This CSS in JS + JS states + CSS selector mix is pure madness.
  control: (provided, state) => ({
    ...provided,
    ...widgetHeight(state.selectProps.uiVariant),
    overflow: 'hidden',
    // Prevent borderRadius overflows.
    boxShadow: false,
    borderBottomLeftRadius: state.selectProps.menuIsOpen ? 0 : provided.borderRadius,
    borderBottomRightRadius: state.selectProps.menuIsOpen ? 0 : provided.borderRadius,
    backgroundColor: tokens.form.widget.surface.color.base,
    borderColor: state.isFocused ? tokens.form.widget.border.color.focus : tokens.form.widget.border.color.base,
    '&:hover': {
      borderColor: tokens.form.widget.border.color.hover
    },
    outline: 'unset',
    '&:has(:focus-visible)': {
      outlineWidth: tokens.outline.width,
      outlineColor: tokens.outline.color,
      outlineStyle: 'solid',
      borderColor: tokens.form.widget.border.color.focus
    },
    // Open menu state.
    ...(state.selectProps.menuIsOpen && {
      '&&, &&:hover': {
        borderBottomColor: tokens.color.neutral['100']
      }
    }),
    // Disabled state.
    ...(state.selectProps.isDisabled && {
      borderColor: tokens.form.widget.border.color.disabled,
      backgroundColor: tokens.form.widget.surface.color.disabled
    }),
    // Readonly state.
    ...(state.selectProps.isReadonly && !state.isFocused && {
      borderStyle: 'dashed'
    }),
    ...(state.selectProps.isReadonly && {
      '&': {
        backgroundColor: tokens.form.widget.surface.color.readonly
      },
      '&, &:not(:has(:focus-visible)):hover': {
        borderColor: tokens.form.widget.border.color.readonly
      }
    })
  }),
  input: provided => ({
    ...provided,
    margin: false,
    paddingTop: false,
    paddingBottom: false,
    '& input': {
      height: 'auto'
    },
    '& input:focus': {
      boxShadow: 'none'
    }
  }),
  menu: (provided, state) => ({
    ...provided,
    borderStyle: 'solid',
    borderColor: tokens.form.widget.border.color.focus,
    borderLeftWidth: 1,
    borderTopWidth: 0,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderRadius: false,
    borderBottomLeftRadius: state.theme.borderRadius,
    borderBottomRightRadius: state.theme.borderRadius,
    boxShadow: false,
    overflow: 'hidden' // Prevent borderRadius overflows.
  }),
  menuList: provided => ({
    ...provided,
    paddingTop: false,
    paddingBottom: false,
    backgroundColor: tokens.color.surface,
    scrollbarWidth: 'thin',
    scrollbarColor: `${tokens.color.neutral.weak} white`,
    transition: 'scrollbar-color 0.2s',
    '&:hover': {
      scrollbarColor: `${tokens.color.neutral.plain} white`
    }
  }),
  indicatorsContainer: () => ({
    flexShrink: 0,
    display: 'flex',
    alignSelf: 'stretch',
    alignItems: 'center'
  }),
  indicatorSeparator: () => ({}),
  dropdownIndicator: () => ({
    display: 'contents',
    // Vertical visual adjustment
    svg: {
      marginRight: tokens.spacing['4x']
    }
  }),
  clearIndicator: () => ({
    flexShrink: 0,
    marginRight: tokens.spacing['2x']
  }),
  loadingIndicator: () => ({
    marginRight: tokens.spacing['7x']
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: false,
    paddingTop: tokens.spacing['1x'],
    paddingRight: tokens.spacing['2x'],
    paddingBottom: tokens.spacing['1x'],
    // Visually align the magnifying icon with options.
    paddingLeft: showSearchIcon(state) ? false : tokens.spacing['4x'],
    ...(state.selectProps.isMulti && {
      gap: tokens.spacing['1x']
    })
  }),
  singleValue: (provided, state) => ({
    ...provided,
    position: false,
    top: false,
    marginLeft: false,
    marginRight: false,
    transform: false,
    ...(state.selectProps.isDisabled && {
      color: 'hsl(231, 72%, 11%, 0.7)'
    }),
    ...(state.selectProps.isReadonly && {
      color: 'hsl(231, 72%, 11%)'
    })
  }),
  option: (provided, state) => ({
    ...provided,
    padding: false,
    paddingTop: tokens.spacing['2x'],
    paddingRight: tokens.spacing['2x'],
    paddingBottom: tokens.spacing['2x'],
    paddingLeft: tokens.spacing['2x'],
    ...(state.data.isDisabled && {
      color: tokens.color.text.weak
    }),
    '&:not(:last-child)': {
      borderBottomStyle: 'solid',
      borderBottomWidth: tokens.border.size.small,
      borderBottomColor: tokens.color.neutral['100']
    }
  }),
  group: () => ({}),
  groupHeading: () => ({
    paddingTop: tokens.spacing['2x'],
    paddingRight: tokens.spacing['2x'],
    paddingBottom: tokens.spacing['2x'],
    paddingLeft: tokens.spacing['2x'],
    textTransform: 'uppercase',
    color: tokens.color.text.weak,
    fontSize: tokens.font.size.xsmall
  }),
  placeholder: provided => ({
    ...provided,
    marginRight: false,
    marginLeft: false,
    position: false,
    top: false,
    transform: false,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  }),
  noOptionsMessage: () => ({
    paddingTop: tokens.spacing['2x'],
    paddingRight: tokens.spacing['2x'],
    paddingBottom: tokens.spacing['2x'],
    paddingLeft: tokens.spacing['2x'],
    backgroundColor: tokens.color.surface,
    color: tokens.color.text.weak
  }),
  loadingMessage: () => ({
    paddingTop: tokens.spacing['2x'],
    paddingRight: tokens.spacing['2x'],
    paddingBottom: tokens.spacing['2x'],
    paddingLeft: tokens.spacing['2x'],
    backgroundColor: tokens.color.surface,
    color: tokens.color.text.weak
  })
};
function DropdownIndicator(props) {
  const {
    innerRef,
    innerProps,
    selectProps: {
      menuIsOpen,
      isDisabled,
      isReadonly,
      uiVariant
    },
    getStyles
  } = props;
  if (isDisabled || isReadonly) return null;
  return _jsx("div", {
    ref: innerRef,
    css: getStyles('dropdownIndicator', props),
    ...innerProps,
    children: _jsx(Svg, {
      name: menuIsOpen ? 'miniChevronTop' : 'miniChevronBottom',
      size: uiVariant === 'small' ? '3x' : '4x',
      theme: "neutral-strong"
    })
  });
}
function ClearIndicator(props) {
  const {
    innerRef,
    innerProps,
    selectProps: {
      isDisabled,
      isReadonly,
      uiVariant
    },
    getStyles
  } = props;
  const {
    __
  } = useTranslator();
  if (isDisabled || isReadonly) return null;
  return _jsx("div", {
    ref: innerRef,
    css: getStyles('clearIndicator', props),
    ...innerProps,
    children: _jsx(AddonButton, {
      intent: "remove",
      size: uiVariant === 'small' ? 'xxs' : 'xs',
      children: __('Clear')
    })
  });
}
function IndicatorSeparator() {
  return null;
}
function LoadingIndicator(props) {
  const {
    innerRef,
    getStyles
  } = props;
  return _jsx(Spinner, {
    name: "dots",
    refCallback: innerRef,
    css: getStyles('loadingIndicator', props)
  });
}
function MultiValue(props) {
  const {
    children,
    removeProps,
    selectProps: {
      isDisabled,
      isReadonly
    }
  } = props;
  return _jsx(Tag, {
    closeButtonProps: removeProps,
    disabled: isDisabled,
    readOnly: isReadonly,
    dismissible: true,
    children: children
  });
}
function Control(props) {
  const {
    selectProps
  } = props;
  const {
    children,
    innerProps,
    ...extraProps
  } = props;
  return _jsxs(defaultComponents.Control, {
    ...extraProps,
    innerProps: {
      ...innerProps,
      ...(selectProps.isReadonly && {
        // Enable copying the value for readOnly state.
        // Although a bit tricky to force setting the focus state, so left as is for now.
        // $FlowFixMe[missing-local-annot]
        onMouseDown: event => {
          event.stopPropagation();
        }
      })
    },
    children: [showSearchIcon(props) && _jsx(Svg, {
      name: "magnifying",
      theme: "neutral-plain",
      size: selectProps.uiVariant === 'small' ? '3x' : '4x',
      css: {
        marginRight: tokens.spacing['2x'],
        marginLeft: tokens.spacing['2x']
      }
    }), children]
  });
}
function Input(props) {
  return _jsx(defaultComponents.Input, {
    ...props,
    css: {
      // Hide filter input blink cursor when the search icon is not visible.
      // Prevent the cursor from showing before the placeholder or after
      // the selected value.
      opacity: showCursor(props) ? 1 : 0
    }
  });
}
function SingleValue(props) {
  const {
    selectProps,
    children
  } = props;
  return _jsx(defaultComponents.SingleValue, {
    ...props,
    children: selectProps.menuIsOpen ? null : children
  });
}
function Placeholder(props) {
  return props.selectProps.menuIsOpen ? null : _jsx(defaultComponents.Placeholder, {
    ...props
  });
}
function MenuList(props) {
  const {
    inputValue
  } = props.selectProps;
  const inputValueRef = React.useRef(inputValue);
  const menuListRef = React.useRef(null);

  // We want to reset the menu list scroll on filtering value change.
  // See https://phabricator.corp.gandi.net/T62724
  React.useEffect(() => {
    if (menuListRef.current instanceof HTMLElement && inputValueRef.current !== inputValue) {
      menuListRef.current.scrollTop = 0;
      inputValueRef.current = inputValue;
    }
  }, [inputValue]);
  return _jsx(defaultComponents.MenuList, {
    ...props,
    // $FlowFixMe[missing-local-annot]
    innerRef: node => {
      props.innerRef(node);
      menuListRef.current = node;
    }
  });
}
function Option(props) {
  const {
    tags,
    value
  } = props.data;
  if (Array.isArray(tags) && tags.length > 0) {
    const {
      children,
      ...extraProps
    } = props;
    return _jsxs(defaultComponents.Option, {
      ...extraProps,
      css: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacing['1x']
      },
      children: [_jsx("span", {
        css: {
          marginRight: tokens.spacing['2x']
        },
        children: children
      }), _jsx(TagList, {
        id: `taglist-${value}`,
        tags: tags,
        max: 8
        // Prevent the "more" tag click to select the option but open the dropdown instead.
        // The `cloneElement` of `DropDownContainer` does not allow event forward for now.
        // So we bind it here.
        // This has the disadvantage, or advantage... to disable clicks on the whole tag list.
        ,
        onClick: evt => evt.stopPropagation()
      })]
    });
  }
  return _jsx(defaultComponents.Option, {
    ...props
  });
}
const components = {
  ClearIndicator,
  IndicatorSeparator,
  DropdownIndicator,
  LoadingIndicator,
  MultiValue,
  SingleValue,
  Control,
  Input,
  Placeholder,
  MenuList,
  Option
};
export { theme, styles, components };